import { subDays, subMonths, subWeeks } from "date-fns";
import xor from "lodash/xor";
import { darken, readableColor } from "polished";
import { MEDIA_TYPE_IMAGE, MEDIA_TYPE_PDF, MEDIA_TYPE_VIDEO, NOW, NOW_MINUS_1_MONTH, NOW_MINUS_1_WEEK, NOW_MINUS_3_DAY, NOW_MINUS_3_MONTH, NOW_MINUS_6_MONTH } from './constants';

/**
 * Convert a date to a relative time string, such as
 * "a minute ago", "in 2 hours", "yesterday", "3 months ago", etc.
 * using Intl.RelativeTimeFormat
 */
export function getRelativeTimeString(
  date,
  lang = navigator.language
) {
  // Allow dates or times to be passed
  const timeMs = typeof date === "number" ? date : date.getTime();

  // Get the amount of seconds between the given date and now
  const deltaSeconds = Math.round((timeMs - Date.now()) / 1000);

  // Array reprsenting one minute, hour, day, week, month, etc in seconds
  const cutoffs = [60, 3600, 86400, 86400 * 7, 86400 * 30, 86400 * 365, Infinity];

  // Array equivalent to the above but in the string representation of the units
  const units = ["second", "minute", "hour", "day", "week", "month", "year"];

  // Grab the ideal cutoff unit
  const unitIndex = cutoffs.findIndex(cutoff => cutoff > Math.abs(deltaSeconds));

  // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
  // is one day in seconds, so we can divide our seconds by this to get the # of days
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;

  // Intl.RelativeTimeFormat do its magic
  const rtf = new Intl.RelativeTimeFormat(lang, { style: "short" });
  return rtf.format(Math.floor(deltaSeconds / divisor), units[unitIndex]);
}

/**
  * Get a date string in the format "YYYY-MM-DDTHH:MM"
  */
export function getDateTimeString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  // Format: "YYYY-MM-DDTHH:MM"
  return `${year}-${month}-${day}T${getTimeString(date)}`;
}

/**
  * Get a time string in the format "HH:MM"
  */
export function getTimeString(date) {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
}

/**
 * Get a date string in the format "YYYY-MM-DD"
 */
export function getDateString(date) {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
  * Convert a local time string to an ISO 8601 time string
  * @param {string} localTime - A time string in the format "HH:MM"
  * @returns {string} An ISO 8601 time string in the format "HH:MM:SS+HH:MM"
  * @example
  * convertToISOTime("15:30") // returns "15:30:00+05:30"
  */
export function convertToISOTime(localTime) {
  // Parse the local time string
  var parts = localTime.split(":");
  var hours = parseInt(parts[0]);
  var minutes = parseInt(parts[1]);

  // Create a Date object with the local time
  var date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0);

  // Calculate the timezone offset in minutes
  var offsetMinutes = date.getTimezoneOffset();

  // Convert the offset to hours and minutes
  var offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
  var offsetMinutesStr = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');

  // Determine the sign of the offset
  var sign = (offsetMinutes < 0 ? "+" : "-");

  // Construct the timezone offset string
  var offsetStr = sign + offsetHours.toString().padStart(2, '0') + ":" + offsetMinutesStr;

  // Convert the time to ISO 8601 format with timezone information
  var isoTime = date.toTimeString().substring(0, 8) + offsetStr;

  return isoTime;
}

/**
  * Convert an ISO 8601 time string to a local time string
  * @param {string} isoTime - An ISO 8601 time string in the format "HH:MM:SS+HH:MM"
  * @returns {string} A local time string in the format "HH:MM"
  * @example
  * convertToLocalTime("10:00:00+05:30") // returns "10:00"
  */
export function convertToLocalTime(isoTime) {
  // Parse the ISO time string
  var isoParts = isoTime.split(":");
  var isoHours = parseInt(isoParts[0]);
  var isoMinutes = parseInt(isoParts[1]);

  // Create a Date object with the ISO time
  var date = new Date();
  date.setHours(isoHours);
  date.setMinutes(isoMinutes);

  // Adjust the time based on the timezone offset

  // Format the local time string
  var localHours = date.getHours().toString().padStart(2, '0');
  var localMinutes = date.getMinutes().toString().padStart(2, '0');
  var localTime = localHours + ":" + localMinutes;

  return localTime;
}

/**
 * Give an integer, return a human-readable string representation of the number
 * @param {number} num - The number to humanize
 * @returns {string} The humanized number
 * @example
 * humanizeNumber(1000) // returns "1k"
 */
export function humanizeNumber(num) {
  if (!num) return "0";
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1) + 'b';
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1) + 'm';
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(1) + 'k';
  } else {
    return num.toString();
  }
};

/**
 * Generate a color scheme based on a base color
 * @param {string} baseColor - The base color to generate the scheme from
 * @returns {object} An object containing the generated color scheme
 * @example
 * getColorScheme("#3498db") // returns { bgColor: "#3498db", borderColor: "#2980b9", textColor: "#fff" }
 */
export function getColorScheme(baseColor) {
  // Generate different shades
  const bgColor = baseColor;
  const borderColor = darken(0.1, baseColor);  // Darker version for border
  const textColor = readableColor(baseColor);  // Auto-generate readable text color (white/black)

  return { bgColor, borderColor, textColor };
}

export function isSetEqual(A, B) {
  return xor(A, B).length === 0;
}

export function to12HourFormat(time24) {
  const [hourStr, minute] = time24.split(":");
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12; // '0' or '12' becomes 12
  return `${hour}:${minute} ${ampm}`;
}

/**
 * Get Date object from relative date string
 */
export function getDateFromRelativeString(date) {
  switch (date) {
    case NOW:
      return new Date()
    case NOW_MINUS_3_DAY:
      return subDays(new Date(), 3)
    case NOW_MINUS_1_WEEK:
      return subWeeks(new Date(), 1)
    case NOW_MINUS_1_MONTH:
      return subMonths(new Date(), 1)
    case NOW_MINUS_3_MONTH:
      return subMonths(new Date(), 3)
    case NOW_MINUS_6_MONTH:
      return subMonths(new Date(), 6)
    default:
      throw new Error("Invalid date string")
  }
}

/**
 * Get LinkedIn post URL from share URN
 */
export function getLinkedinPostUrl(shareUrn) {
  if (!shareUrn) {
    return null;
  }
  return `https://www.linkedin.com/feed/update/${shareUrn}`;
}

/**
 * Get post content from the variations
 * By default, return the first variation's content
 */
export function getPostContent(post, index = 0) {
  return post?.Variations?.[index]?.Content
}

export function countTotalVariations(variations) {
  if (!variations) return 0;
  return variations.reduce((count, v) => count + 1 + (v.Reposts?.length || 0), 0);
}

/**
 * Get post media from the variations
 * By default, return the first variation's media
 */
export function getPostMedias(post, index = 0) {
  return post?.Variations?.[index]?.Medias
}

/**
 * Get post comment from the variations
 * By default, return the first variation's comment
 */
export function getPostComment(post, index = 0) {
  return post?.Variations?.[index]?.Comment
}

/**
  * Get media type from media URN
  * E.g. "urn:li:image:C4D00AAAAbAAAQWAAA" -> "image"
  *      "urn:li:video:V4D00AAAAbAAAQWAAA" -> "video"
  *      "urn:li:document:PDF00AAAAbAAAQWAAA" -> "pdf"
  */

export function getMediaType(urn) {
  const urnSplits = urn.split(":");
  if (urnSplits.length < 3) return "unknown"
  const type = urnSplits[2]
  if (type === "image") {
    return MEDIA_TYPE_IMAGE
  }
  if (type === "video") {
    return MEDIA_TYPE_VIDEO
  }
  if (type === "document") {
    return MEDIA_TYPE_PDF
  }
  return "unknown"
}

/**
 * Format LinkedIn post content by converting LinkedIn-specific syntax to human-readable format
 * E.g. convert {hashtag|\#|mytag} to #mytag, convert @[Eddy](urn:li:person:123456789) to Eddy
 */
export function formatLinkedinPostContent(content) {
  // convert {hashtag|\#|mytag} to #mytag
  // convert @[Eddy](urn:li:person:123456789) to Eddy
  return content
    .replace(/{hashtag\|\\#\|([^}]+)}/g, '#$1')
    .replace(/@\[(.*?)\]\(urn:li:.+:.+\)/g, '$1');
}

export function cleanLinkedInShareCommentary(text) {
  return (text || '').split('\n').map(line => line.replace(/^"|"$/g, '')).join('\n').trim()
}

export function parseCSV(text) {
  const rows = []
  const len = text.length
  let pos = 0

  const parseField = () => {
    let field = ''
    if (pos < len && text[pos] === '"') {
      pos++
      while (pos < len) {
        if (text[pos] === '"') {
          if (pos + 1 < len && text[pos + 1] === '"') {
            field += '"'
            pos += 2
          } else {
            pos++
            break
          }
        } else {
          field += text[pos++]
        }
      }
    } else {
      while (pos < len && text[pos] !== ',' && text[pos] !== '\n' && text[pos] !== '\r') {
        field += text[pos++]
      }
    }
    return field
  }

  const parseRow = () => {
    const fields = []
    while (pos < len) {
      fields.push(parseField())
      if (pos >= len || text[pos] === '\n' || text[pos] === '\r') break
      if (text[pos] === ',') pos++
    }
    if (pos < len && text[pos] === '\r') pos++
    if (pos < len && text[pos] === '\n') pos++
    return fields
  }

  if (pos >= len) return rows
  const headers = parseRow().map(h => h.trim())

  while (pos < len) {
    if (text[pos] === '\r') { pos++; continue }
    if (text[pos] === '\n') { pos++; continue }
    const values = parseRow()
    if (values.length !== headers.length) continue
    const row = {}
    headers.forEach((h, idx) => { row[h] = values[idx] })
    rows.push(row)
  }

  return rows
}
